// ChartControl5.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import * as Highcharts from "highcharts";
import HighchartsGantt from "highcharts/modules/gantt";
import DragDrop from "highcharts/modules/draggable-points";
import Accessibility from "highcharts/modules/accessibility";
import HighchartsReact from "highcharts-react-official";

import AppContext from "../contexts/AppContext";
import WithLoader from "./WithLoader";
import DrillModalDialog from "./DrillModalDialog";
import EmptyTransactionIcon from "../icons/EmptyTransactionIcon";
import appFetch from "../appFetch";
import ConfirmDialog from "./ConfirmDialog";

// Init Gantt modules
HighchartsGantt(Highcharts);
DragDrop(Highcharts);
Accessibility(Highcharts);

const EmptyRecord = () => (
  <div className="grid place-content-center place-items-center h-96 bg-gray-100 rounded-sm mb-5">
    <EmptyTransactionIcon />
    <h2 className="font-semibold text-md">No chart available</h2>
  </div>
);

function ChartControl5({
  setLoading,
  name,
  options,
  selectedChartControl,
  filterOption,
  accessOption,
}) {
  const [chartRef, setChartRef] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subtitle: "",
    schema: [],
  });
  const [chartKey, setChartKey] = useState(0);
  const [originalData, setOriginalData] = useState([]);

  const { context } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  const graphData = (selectedChartControl?.options ?? options)?.[0] ?? {};
  const day = 1000 * 60 * 60 * 24;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const startDate = today.getTime();

  useEffect(() => {
    const data = [
      {
        start: startDate + 2 * day,
        end: startDate + 5 * day,
        name: "Prototype",
        id: "prototype",
        y: 0,
      },
      {
        start: startDate + 6 * day,
        name: "Prototype done",
        milestone: true,
        dependency: "prototype",
        id: "proto_done",
        y: 0,
      },
      {
        start: startDate + 7 * day,
        end: startDate + 11 * day,
        name: "Testing",
        dependency: "proto_done",
        y: 0,
      },
      {
        start: startDate + 5 * day,
        end: startDate + 8 * day,
        name: "Product pages",
        y: 1,
      },
      {
        start: startDate + 9 * day,
        end: startDate + 10 * day,
        name: "Newsletter",
        y: 1,
      },
      {
        start: startDate + 9 * day,
        end: startDate + 11 * day,
        name: "Licensing",
        id: "testing",
        y: 2,
      },
      {
        start: startDate + 11.5 * day,
        end: startDate + 12.5 * day,
        name: "Publish",
        dependency: "testing",
        y: 2,
      },
    ];

    setInitialData(data.map((d) => ({ ...d })));
    setOriginalData(data.map((d) => ({ ...d })));
  }, []);

//   console.log("the original data", originalData);

  const showDetail = (point) => {
    setLoading(true);
    // Fetch detailed data (similar to ChartControl1)
    setTimeout(() => {
      setModalData({ ...point });
      setIsOpen(true);
      setLoading(false);
    }, 800);
  };

  const handleSaveChanges = () => {
    if (!chartRef) return;

    const updatedData = chartRef.series[0].data.map((point) => ({
      id: point.id,
      name: point.name,
      y: point.y,
      start: point.start,
      end: point.end,
      milestone: point.milestone || false,
      dependency: point.dependency || null,
    }));

    console.log("Updated Task Data:", updatedData);

    setLoading(true);
    appFetch
      .post("/your-api/endpoint/to/save-tasks", {
        layoutId: graphData.layoutid,
        tasks: updatedData,
      })
      .then((res) => {
        setHasChanges(false);
        alert("Schedule saved successfully!");
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to save schedule.");
      })
      .finally(() => setLoading(false));
  };

  const handleClearChanges = () => {
    setInitialData(originalData.map((d) => ({ ...d })));
    setChartKey((prev) => prev + 1);
    setHasChanges(false);
  };

  const ganttOptions = {
    chart: {
      type: "gantt",
    },
    title: {
      text: "Interactive Gantt Chart",
    },
    subtitle: {
      text: "Drag and drop tasks to reschedule",
    },
    xAxis: {
      currentDateIndicator: true,
    },
    yAxis: {
      type: "category",
      categories: ["Tech", "Marketing", "Sales"],
      min: 0,
      max: 2,
    },
    tooltip: {
      xDateFormat: "%a %b %d, %H:%M",
    },
    accessibility: {
      point: {
        descriptionFormat:
          "{#if milestone}" +
          "{name}, milestone for {yCategory} at {x:%Y-%m-%d}." +
          "{else}" +
          "{name}, assigned to {yCategory} from {x:%Y-%m-%d} to {x2:%Y-%m-%d}." +
          "{/if}",
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600, // adjust based on your breakpoint
          },
          chartOptions: {
            xAxis: {
              labels: {
                rotation: -45,
                style: { fontSize: "10px" },
              },
            },
            yAxis: {
              labels: {
                style: { fontSize: "10px" },
              },
            },
            chart: {
              spacingLeft: 5,
              spacingRight: 5,
            },
            plotOptions: {
              series: {
                dataLabels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            },
          },
        },
      ],
    },

    plotOptions: {
      series: {
        dragDrop: {
          draggableX: true,
          draggableY: true,
          dragPrecisionX: day / 3,
          dragMinY: 0,
          dragMaxY: 2,
        },
        allowPointSelect: true,
        point: {
          events: {
            select() {
              showDetail(this);
            },
            drop() {
              setHasChanges(true);
            },
          },
        },
        dataLabels: {
          enabled: true,
          format: "{point.name}",
        },
      },
    },
    series: [
      {
        name: "Project 1",
        data: [...initialData],
      },
    ],
  };

  return (
    <>
      {isOpen && (
        <DrillModalDialog
          isOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          item={modalData}
          setLoading={setLoading}
          dateFilter={filterOption}
          accessFilter={accessOption}
        />
      )}

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <div className="overflow-auto w-full mb-10">
        <div id="gantt-graph" className="mb-10">
          {ganttOptions.series[0].data.length > 0 ? (
            <HighchartsReact
              key={chartKey}
              highcharts={Highcharts}
              constructorType="ganttChart"
              options={ganttOptions}
              containerProps={{ style: { width: "100%" } }}
            />
          ) : (
            <EmptyRecord />
          )}
        </div>

        {hasChanges && (
          <div className="flex md:justify-end gap-2">
            <button
              type="button"
              className="justify-self-end bg-primary px-2 py-2 md:py-2 md:px-5 text-white rounded-md text-xs md:text-sm font-medium capitalize"
              onClick={() => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Are you sure you want to Proceed?",
                  subtitle: "You can't undo this operation",
                  onConfirm: async () => {
                    await handleSaveChanges();
                  },
                });
              }}
            >
              Save Changes
            </button>
            <button
              type="button"
              className="justify-self-end bg-white border-2 border-primary px-2 py-2 md:py-2 md:px-5 text-primary text-xs md:text-sm rounded-md"
              onClick={handleClearChanges}
            >
              Clear
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default WithLoader(ChartControl5);
